/**
 * Client-side SHA-256 of an audio File using the Web Crypto API.
 * Returns a lowercase hex string.
 *
 * Browser-safe — never import from Deno/server code.
 */

/** Read a File/Blob into a Uint8Array, working around jsdom's missing .arrayBuffer(). */
async function fileToUint8Array(file: File): Promise<Uint8Array> {
  // Modern browsers & happy-dom: File.arrayBuffer() is available
  if (typeof file.arrayBuffer === "function") {
    const raw = await file.arrayBuffer();
    return new Uint8Array(raw);
  }
  // Fallback: FileReader (also works in jsdom)
  return new Promise<Uint8Array>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (result instanceof ArrayBuffer) {
        resolve(new Uint8Array(result));
      } else if (result && typeof result === "object" && "length" in result) {
        // Node Buffer returned by jsdom's FileReader
        resolve(new Uint8Array(result as ArrayBufferLike));
      } else {
        reject(new Error("Unexpected FileReader result"));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

export async function fileSha256(file: File): Promise<string> {
  const bytes = await fileToUint8Array(file);
  // crypto.subtle.digest accepts TypedArray directly
  const hashBuffer = await crypto.subtle.digest("SHA-256", bytes);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}
